import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { FaExpand, FaCompress } from 'react-icons/fa';
import Select from 'react-select';

// SVG Icons (unchanged)
const CloseIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);
const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);
const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);
const PlusIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);
const TrashIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);
const CheckIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
);
const SaveIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"></path>
    <polyline points="17 21 17 13 7 13 7 21"></polyline>
    <polyline points="7 3 7 8 15 8"></polyline>
  </svg>
);
const AutofillIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);

const API_URL = '/api';

// Status options - UPDATED to show Pending/Approved
const STATUS_OPTIONS = [
  { value: '1', label: 'Approved' },
  { value: '0', label: 'Pending' },
];

// Payment terms options
const PAYMENT_TERMS_OPTIONS = [
  { value: '', label: 'Select Payment Terms' },
  { value: 'Cash', label: 'Cash' },
  { value: 'Credit', label: 'Credit' },
  { value: 'Card', label: 'Card' },
  { value: 'Other', label: 'Other' },
];

// Linked document options
const LINKED_DOCUMENT_OPTIONS = [
  { value: '', label: 'Select Document Type' },
  { value: 'SO', label: 'Sales Order' },
  { value: 'PR', label: 'Purchase Requisition' },
  { value: 'FR', label: 'Forecast Requisition' },
  { value: 'RFQ', label: 'RFQ' },
];

// ERP Command Constants
const ERP_COMMANDS = {
  SAVE_POPUP_ITEMS: 'SAVE_POPUP_ITEMS',
  LOAD_SAVED_ITEMS: 'LOAD_SAVED_ITEMS',
  DELETE_SAVED_ITEMS: 'DELETE_SAVED_ITEMS',
  AUTOFILL_ITEMS: 'AUTOFILL_ITEMS'
};

// Helper function to get linked document label from value
const getLinkedDocumentLabel = (value) => {
  // Handle both string and array formats
  const docType = Array.isArray(value) ? value[0] : value;
  const option = LINKED_DOCUMENT_OPTIONS.find(opt => opt.value === docType);
  return option ? option.label : docType || '';
};

const initialHeader = {
  poNumber: '',
  poDate: new Date().toISOString().split('T')[0],
  vendorCode: '',
  vendorAddressId: '',
  vendorContactId: '',
  currencyCode: '',
  deliveryDate: '',
  paymentTerms: '',
  remarks: '',
  locationCode: '',
  BranchCode: '',
  is_active: '1',
  SubTotal: '0',
  TotalDiscount: '0',
  TotalTax: '0',
  TotalAmount: '0',
  LinkedDocumentType: '',
  LinkedDocumentNumber: '',
  department_id: '' // Added department_id field
};

const initialItem = {
  itemCode: '',
  productCode: '',
  productName: '',
  description: '',
  uom: '',
  quantity: '',
  unitPrice: '',
  discount: '0',
  taxCode: '',
  taxRate: '0', // Add this line
  total: '0',
  taxAmount: '0',
  remainingQuantity: '',
  mrnReceivedQty: '0',
  LinkedDocumentType: '',
  LinkedDocumentNumber: '',
  LinkedItemCode: '',
  isLinked: false, // NEW: Flag to track if item is linked
  department_id: '' // Added department_id field
};

// Multi-Select Product Modal Component
const ProductMultiSelectModal = ({ 
  open, 
  onClose, 
  products, 
  onConfirm,
  onSaveToTable,
  onAutofill 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isSaving, setIsSaving] = useState(false);
  const listRef = useRef(null);
  const searchInputRef = useRef(null);
  const tableBodyRef = useRef(null);

  // Filter products based on search term
  const filteredProducts = useMemo(() => {
    if (!searchTerm) return products;
    const term = searchTerm.toLowerCase();
    return products.filter(product => {
      return (
        (product.ProductCode && product.ProductCode.toLowerCase().includes(term)) || 
        (product.ProductName && product.ProductName.toLowerCase().includes(term)) ||
        (product.Status && product.Status.toLowerCase().includes(term)) ||
        (product.CategoryName && product.CategoryName.toLowerCase().includes(term)) ||
        (product.UOMName && product.UOMName.toLowerCase().includes(term)) ||
        (product.Description && product.Description.toLowerCase().includes(term)) ||
        (product.BranchName && product.BranchName.toLowerCase().includes(term)) ||
        (product.DepartmentName && product.DepartmentName.toLowerCase().includes(term)) ||
        (product.AllowProductNegativeInventory !== undefined && 
         (product.AllowProductNegativeInventory ? 'yes' : 'no').includes(term)) ||
        (product.AllowBatchOnProduct !== undefined && 
         (product.AllowBatchOnProduct ? 'yes' : 'no').includes(term)) ||
        (product.ProductType && product.ProductType.toLowerCase().includes(term)) ||
        (product.ProductionBOMType && product.ProductionBOMType.toLowerCase().includes(term))
      );
    });
  }, [products, searchTerm]);

  // Check if all filtered products are selected
  const allFilteredSelected = useMemo(() => {
    if (filteredProducts.length === 0) return false;
    return filteredProducts.every(product => selectedProducts.includes(product.ProductCode));
  }, [filteredProducts, selectedProducts]);

  // Check if some filtered products are selected (for indeterminate state)
  const someFilteredSelected = useMemo(() => {
    if (filteredProducts.length === 0) return false;
    return filteredProducts.some(product => selectedProducts.includes(product.ProductCode)) && !allFilteredSelected;
  }, [filteredProducts, selectedProducts, allFilteredSelected]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (open && tableBodyRef.current && highlightedIndex >= 0) {
      const items = tableBodyRef.current.querySelectorAll('.product-row');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex, open]);

  // Reset and focus when modal opens
  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setSelectedProducts([]);
      setHighlightedIndex(-1);
      
      // Focus search input after a short delay
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }
  }, [open]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    // Handle search input keys
    if (e.target === searchInputRef.current) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex(0);
        // Move focus to the table body
        setTimeout(() => {
          if (tableBodyRef.current) {
            tableBodyRef.current.focus();
          }
        }, 0);
        return;
      }
      return;
    }

    // Handle table body navigation
    if (e.target === tableBodyRef.current) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex(prev => {
            if (prev < filteredProducts.length - 1) {
              return prev + 1;
            } else if (filteredProducts.length > 0) {
              return 0; // Loop back to first item
            }
            return prev;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex(prev => {
            if (prev > 0) {
              return prev - 1;
            } else if (filteredProducts.length > 0) {
              return filteredProducts.length - 1; // Loop to last item
            }
            return prev;
          });
          break;
        case ' ':
          e.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < filteredProducts.length) {
            toggleProductSelection(filteredProducts[highlightedIndex].ProductCode);
          }
          break;
        case 'Enter':
          e.preventDefault();
          if (e.ctrlKey) {
            // Ctrl+Enter for autofill
            handleAutofill();
          } else if (highlightedIndex >= 0 && highlightedIndex < filteredProducts.length) {
            // Regular Enter for selecting highlighted product
            toggleProductSelection(filteredProducts[highlightedIndex].ProductCode);
          }
          break;
        case 'Escape':
          onClose();
          break;
        default:
          break;
      }
    }
  };

  // Toggle product selection
  const toggleProductSelection = (productCode) => {
    setSelectedProducts(prev => 
      prev.includes(productCode)
        ? prev.filter(code => code !== productCode)
        : [...prev, productCode]
    );
  };

  // Handle select all filtered products
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Add all filtered products to selection
      const allProductCodes = filteredProducts.map(p => p.ProductCode);
      setSelectedProducts(prev => [...new Set([...prev, ...allProductCodes])]);
    } else {
      // Remove all filtered products from selection
      const filteredProductCodes = filteredProducts.map(p => p.ProductCode);
      setSelectedProducts(prev => prev.filter(code => !filteredProductCodes.includes(code)));
    }
  };

  // Handle clear all selection
  const handleClearAll = () => {
    setSelectedProducts([]);
  };

  // Handle confirm selection
  const handleConfirm = () => {
    onConfirm(selectedProducts);
    onClose();
  };

  // Handle save to table
  const handleSaveToTable = async () => {
    if (selectedProducts.length === 0) return;
    
    setIsSaving(true);
    try {
      await onSaveToTable(selectedProducts);
      alert('Items saved to table successfully!');
    } catch (error) {
      console.error('Error saving to table:', error);
      alert('Failed to save items to table');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Handle autofill
  const handleAutofill = () => {
    if (selectedProducts.length === 0) return;
    
    try {
      const result = onAutofill(selectedProducts);
      
      if (result && result.success) {
        // Success
      } else {
        alert(result?.error || 'Error autofilling table. Please try again.');
      }
    } catch (error) {
      console.error('Error during autofill:', error);
      alert('Error autofilling table. Please try again.');
    } finally {
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-2xl w-full  h-[100vh] flex flex-col border border-gray-200">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-gray-800">Select Multiple Products</h3>
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded-full hover:bg-gray-200"
            >
              <CloseIcon size={24} />
            </button>
          </div>
        </div>
        
        <div className="p-5 border-b border-gray-200 bg-white">
          <div className="relative">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by Product Code, Name, Status, Category, UOM, Description, Branch, Department, etc..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base shadow-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <div className="absolute right-3 top-3 text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          <div className="mt-3 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg border border-blue-100">
            <div className="font-medium text-blue-800 mb-1">Keyboard Shortcuts:</div>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center px-2 py-1 bg-white rounded-md text-xs font-medium text-gray-700 border border-gray-200">
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">↑</kbd>
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">↓</kbd>
                <span className="ml-1">Navigate</span>
              </span>
              <span className="inline-flex items-center px-2 py-1 bg-white rounded-md text-xs font-medium text-gray-700 border border-gray-200">
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">Space</kbd>
                <span className="ml-1">Select</span>
              </span>
              <span className="inline-flex items-center px-2 py-1 bg-white rounded-md text-xs font-medium text-gray-700 border border-gray-200">
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">Enter</kbd>
                <span className="ml-1">Select</span>
              </span>
              <span className="inline-flex items-center px-2 py-1 bg-white rounded-md text-xs font-medium text-gray-700 border border-gray-200">
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">Ctrl</kbd>
                <span className="mx-1">+</span>
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">Enter</kbd>
                <span className="ml-1">Autofill</span>
              </span>
            </div>
          </div>
        </div>
        
        <div 
          ref={listRef}
          className="flex-grow overflow-y-auto bg-gray-50 relative"
        >
          {filteredProducts.length > 0 ? (
            <div className="h-full flex flex-col">
              {/* Single Table with Sticky Header */}
              <div className="flex-grow overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  {/* Sticky Header */}
                  <thead className="sticky top-0 z-30 bg-gray-100 shadow-md">
                    <tr>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-12">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                          checked={allFilteredSelected}
                          ref={(el) => {
                            if (el) {
                              el.indeterminate = someFilteredSelected;
                            }
                          }}
                          onChange={handleSelectAll}
                          title={allFilteredSelected ? "Deselect all" : someFilteredSelected ? "Select all filtered" : "Select all filtered"}
                        />
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-32">
                        Product Code
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider min-w-[200px]">
                        Product Name
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-24">
                        Status
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        Category
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-20">
                        UOM
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider min-w-[180px]">
                        Description
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        Branch
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        Department
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-32">
                        Negative Inv.
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-24">
                        Batch
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        Product Type
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        BOM Type
                      </th>
                    </tr>
                  </thead>
                  
                  {/* Scrollable Body */}
                  <tbody 
                    ref={tableBodyRef}
                    className="bg-white divide-y divide-gray-200 focus:outline-none"
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                  >
                    {filteredProducts.map((product, index) => (
                      <tr
                        key={product.ProductCode}
                        className={`product-row hover:bg-blue-50 transition-colors ${
                          highlightedIndex === index ? 'bg-blue-100' : ''
                        } ${selectedProducts.includes(product.ProductCode) ? 'bg-blue-50' : ''}`}
                        onClick={() => toggleProductSelection(product.ProductCode)}
                        onMouseEnter={() => setHighlightedIndex(index)}
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                            checked={selectedProducts.includes(product.ProductCode)}
                            onChange={() => {}}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 w-32">
                          {product.ProductCode}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium min-w-[200px]">
                          {product.ProductName}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm w-24">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            product.Status === 'Active' ? 'bg-green-100 text-green-800' : 
                            product.Status === 'Inactive' ? 'bg-red-100 text-red-800' : 
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {product.Status || 'N/A'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.CategoryName || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-20">
                          {product.UOMName || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate min-w-[180px]" title={product.Description || ''}>
                          {product.Description || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.BranchName || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.DepartmentName || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm w-32">
                          {product.AllowProductNegativeInventory !== undefined ? (
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              product.AllowProductNegativeInventory ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {product.AllowProductNegativeInventory ? 'Yes' : 'No'}
                            </span>
                          ) : 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm w-24">
                          {product.AllowBatchOnProduct !== undefined ? (
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              product.AllowBatchOnProduct ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {product.AllowBatchOnProduct ? 'Yes' : 'No'}
                            </span>
                          ) : 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.ProductType || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.ProductionBOMType || 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full py-16">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">No products found</h3>
              <p className="mt-2 text-sm text-gray-500">
                {products.length === 0 
                  ? 'No products are available in the system.' 
                  : 'No products match your search criteria. Try different keywords.'}
              </p>
            </div>
          )}
        </div>
        
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-700">
              <span className="font-medium">{selectedProducts.length}</span> product{selectedProducts.length !== 1 ? 's' : ''} selected
            </div>
            {selectedProducts.length > 0 && (
              <button
                type="button"
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                onClick={handleClearAll}
              >
                Clear All
              </button>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={handleSaveToTable}
              disabled={selectedProducts.length === 0 || isSaving}
            >
              <SaveIcon className="mr-2 h-4 w-4" />
              {isSaving ? 'Saving...' : 'Save to Table'}
            </button>
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={handleAutofill}
              disabled={selectedProducts.length === 0}
            >
              <AutofillIcon className="mr-2 h-4 w-4" />
              Autofill Table
            </button>
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              onClick={handleConfirm}
              disabled={selectedProducts.length === 0}
            >
              Add Selected
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Reusable Searchable Dropdown Component with Auto-Focus on Search
const SearchableDropdown = ({ 
  options, 
  value, 
  onChange, 
  placeholder = "Select",
  error = false,
  width = "w-full",
  getOptionLabel = (option) => option.name || option.label || option.toString(),
  getOptionValue = (option) => option.value || option.id || option.toString(),
  getOptionSecondaryLabel = (option) => option.code || option.id || "",
  disabled = false,
  className = "",
  onMultiSelectTrigger = null,
  multiSelectShortcut = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownListRef = useRef(null);
  const searchInputRef = useRef(null);

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width
      });
    }
  }, [isOpen]);

  // Handle window resize and scroll to update position
  useEffect(() => {
    if (!isOpen) return;
    
    const handleUpdatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width
        });
      }
    };
    
    window.addEventListener('resize', handleUpdatePosition);
    window.addEventListener('scroll', handleUpdatePosition);
    
    return () => {
      window.removeEventListener('resize', handleUpdatePosition);
      window.removeEventListener('scroll', handleUpdatePosition);
    };
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && dropdownListRef.current && highlightedIndex >= 0) {
      const items = dropdownListRef.current.querySelectorAll('.dropdown-item');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Clear search term and reset highlighted index when opening
      setSearchTerm('');
      setHighlightedIndex(-1);
      
      // Focus the search input after a short delay to ensure it's rendered
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 0);
    }
  }, [isOpen]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const term = searchTerm.toLowerCase();
    
    return options.filter(option => {
      const label = getOptionLabel(option).toLowerCase();
      const secondaryLabel = getOptionSecondaryLabel(option).toLowerCase();
      
      return label.includes(term) || secondaryLabel.includes(term);
    });
  }, [options, searchTerm, getOptionLabel, getOptionSecondaryLabel]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    // Check for multi-select shortcut
    if (onMultiSelectTrigger && multiSelectShortcut && e.ctrlKey && e.key === multiSelectShortcut) {
      e.preventDefault();
      onMultiSelectTrigger();
      return;
    }

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev < filteredOptions.length - 1) {
            return prev + 1;
          } else if (filteredOptions.length > 0) {
            return 0; // Loop back to first item
          }
          return prev;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev > 0) {
            return prev - 1;
          } else if (filteredOptions.length > 0) {
            return filteredOptions.length - 1; // Loop to last item
          }
          return prev;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleSelectOption(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const handleSelectOption = (option) => {
    onChange(getOptionValue(option));
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get selected option
  const selectedOption = options.find(option => getOptionValue(option) === value);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        className={`${width} border rounded-lg px-3 py-2 cursor-pointer flex justify-between items-center ${
          error ? 'border-red-400' : 'border-[#E2E8F0]'
        } ${isOpen ? 'ring-1 ring-[#BDDDFC]' : ''} ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        ref={triggerRef}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <span className="truncate">
          {selectedOption ? getOptionLabel(selectedOption) : placeholder}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      
      {isOpen && (
        <div 
          className="fixed z-[9999] bg-white border border-[#E2E8F0] rounded-lg shadow-lg"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${position.width}px`,
          }}
        >
          <div className="p-2 border-b border-[#E2E8F0]">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search..."
              className="w-full px-2 py-1 border border-[#E2E8F0] rounded focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          
          <div 
            className="max-h-60 overflow-y-auto" 
            ref={dropdownListRef}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={getOptionValue(option)}
                  className={`dropdown-item px-3 py-2 cursor-pointer hover:bg-[#F1F5F9] ${
                    highlightedIndex === index ? 'bg-[#BDDDFC]' : ''
                  } ${value === getOptionValue(option) ? 'bg-[#E6F0FF]' : ''}`}
                  onClick={() => handleSelectOption(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="font-medium">{getOptionLabel(option)}</div>
                  {getOptionSecondaryLabel(option) && (
                    <div className="text-xs text-gray-500">{getOptionSecondaryLabel(option)}</div>
                  )}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500">No options found</div>
            )}
          </div>
        </div>
      )}
      
      {error && <span className="text-xs text-red-500">*</span>}
    </div>
  );
};

// Sales Items Modal Component (unchanged)
const SalesItemsModal = ({ 
  open, 
  onClose, 
  onItemsSelected,
  existingItems 
}) => {
  const [salesItems, setSalesItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  // Add state to track linked sales item codes
  const [linkedSalesItemCodes, setLinkedSalesItemCodes] = useState(new Set());

  // Fetch all sales items when modal opens
  useEffect(() => {
    if (open) {
      fetchSalesItems();
      fetchLinkedSalesItems(); // Add this call
    }
  }, [open]);

  // Add function to fetch linked sales items
  const fetchLinkedSalesItems = async () => {
    try {
      const response = await fetch(`${API_URL}/purchase-order`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      if (response.ok) {
        const allPOs = await response.json();
        const codes = new Set();
        
        allPOs.forEach(po => {
          if (po.Items) {
            po.Items.forEach(item => {
              if (item.LinkedItemCode) {
                codes.add(item.LinkedItemCode);
              }
            });
          }
        });
        
        setLinkedSalesItemCodes(codes);
      }
    } catch (error) {
      console.error('Error fetching linked sales items:', error);
    }
  };

  // Fetch sales items function (was missing)
  const fetchSalesItems = async () => {
    setLoading(true);
    try {
      // First get all sales orders
      const salesOrdersResponse = await fetch(`${API_URL}/sales-order`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      if (salesOrdersResponse.ok) {
        const salesOrders = await salesOrdersResponse.json();
        
        // Then get items for each sales order
        const allItems = [];
        for (const order of salesOrders) {
          const itemsResponse = await fetch(`${API_URL}/sales-order/${order.SalesOrderNumber}/items`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
          if (itemsResponse.ok) {
            const items = await itemsResponse.json();
            // Add sales order info to each item
            const itemsWithOrderInfo = items.map(item => ({
              ...item,
              SalesOrderNumber: order.SalesOrderNumber,
              SalesOrderDate: order.SalesOrderDate,
              CustomerCode: order.CustomerCode
            }));
            allItems.push(...itemsWithOrderInfo);
          }
        }
        
        setSalesItems(allItems);
        setFilteredItems(allItems);
        console.log('Fetched sales items:', allItems);
      }
    } catch (error) {
      console.error('Error fetching sales items:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter items based on search term and linked status
  useEffect(() => {
    if (!searchTerm) {
      // Filter out items that are linked to any PO
      const availableItems = salesItems.filter(item => 
        !linkedSalesItemCodes.has(item.SalesItemCode)
      );
      setFilteredItems(availableItems);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = salesItems.filter(item => 
        (item.ProductName?.toLowerCase().includes(term) ||
        item.ProductCode?.toLowerCase().includes(term) ||
        item.Description?.toLowerCase().includes(term) ||
        item.SalesOrderNumber?.toLowerCase().includes(term)) &&
        !linkedSalesItemCodes.has(item.SalesItemCode) // Add this condition
      );
      setFilteredItems(filtered);
    }
  }, [searchTerm, salesItems, linkedSalesItemCodes]);

  const handleItemSelect = (item) => {
    const isSelected = selectedItems.some(si => si.SalesItemCode === item.SalesItemCode);
    if (isSelected) {
      setSelectedItems(selectedItems.filter(si => si.SalesItemCode !== item.SalesItemCode));
    } else {
      setSelectedItems([...selectedItems, item]);
    }
  };

  const handleSelectAll = () => {
    if (selectedItems.length === filteredItems.length) {
      setSelectedItems([]);
    } else {
      // Only select items that aren't linked to any PO
      const availableItems = filteredItems.filter(item => 
        !linkedSalesItemCodes.has(item.SalesItemCode)
      );
      setSelectedItems([...availableItems]);
    }
  };

  const handleClearSelection = () => {
    setSelectedItems([]);
  };

  // Add selected items function (was missing)
  const handleAddSelectedItems = () => {
    if (selectedItems.length > 0) {
      onItemsSelected(selectedItems);
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-center items-center">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      <div className="bg-white shadow-2xl flex flex-col w-full max-w-6xl h-[90vh] rounded-lg z-10">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold text-[#384959]">
            Select Sales Items
          </h2>
          <button onClick={onClose} className="text-[#A0AEC0] hover:text-[#384959]">
            <CloseIcon size={24} />
          </button>
        </div>
        
        <div className="flex-grow overflow-y-auto p-6">
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search items by name, code, description, or order number..."
                    className="w-full pl-10 pr-4 py-2 border border-[#E2E8F0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                  </svg>
                </div>
              </div>
              <div className="flex gap-2 ml-4">
                <button
                  onClick={handleSelectAll}
                  className="px-4 py-2 border border-[#E2E8F0] rounded-lg hover:bg-[#F1F5F9] transition-colors flex items-center"
                >
                  <input
                    type="checkbox"
                    checked={selectedItems.length === filteredItems.length && filteredItems.length > 0}
                    onChange={handleSelectAll}
                    className="mr-2 h-4 w-4 text-[#6A89A7] rounded"
                  />
                  Select All
                </button>
                <button
                  onClick={handleClearSelection}
                  disabled={selectedItems.length === 0}
                  className="px-4 py-2 border border-[#E2E8F0] rounded-lg hover:bg-[#F1F5F9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Clear Selection
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              Showing {filteredItems.length} of {salesItems.length} items. {selectedItems.length} selected.
              {/* Add this message */}
              {linkedSalesItemCodes.size > 0 && (
                <span className="ml-2 text-red-500">
                  ({linkedSalesItemCodes.size} items already linked to POs)
                </span>
              )}
            </p>
          </div>
          
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6A89A7] mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading sales items...</p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full border border-[#E2E8F0]">
                <thead>
                  <tr className="bg-[#BDDDFC] text-[#384959]">
                    <th className="p-3 w-12">
                      <input
                        type="checkbox"
                        checked={selectedItems.length === filteredItems.length && filteredItems.length > 0}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-[#6A89A7] rounded"
                      />
                    </th>
                    <th className="p-3 text-left">Item Code</th>
                    <th className="p-3 text-left">Product Name</th>
                    <th className="p-3 text-left">Description</th>
                    <th className="p-3 text-left">Sales Order</th>
                    <th className="p-3 text-left">Customer</th>
                    <th className="p-3 text-left">UOM</th>
                    <th className="p-3 text-right">Quantity</th>
                    <th className="p-3 text-right">Unit Price</th>
                    <th className="p-3 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.length > 0 ? (
                    filteredItems.map(item => {
                      // Check if item is already in the existing items
                      const isAlreadyAdded = existingItems.some(
                        existingItem => existingItem.LinkedItemCode === item.SalesItemCode
                      );
                      
                      // NEW: Check if item is linked to any PO
                      const isLinkedToAnyPO = linkedSalesItemCodes.has(item.SalesItemCode);
                      
                      const isSelected = selectedItems.some(si => si.SalesItemCode === item.SalesItemCode);
                      
                      return (
                        <tr 
                          key={item.SalesItemCode} 
                          className={`border-t border-[#E2E8F0] ${
                            isAlreadyAdded || isLinkedToAnyPO ? 'bg-gray-100' : 
                            isSelected ? 'bg-blue-50' : 'hover:bg-[#F1F5F9]'
                          } cursor-pointer`}
                          onClick={() => !(isAlreadyAdded || isLinkedToAnyPO) && handleItemSelect(item)}
                        >
                          <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                            <input 
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleItemSelect(item)}
                              disabled={isAlreadyAdded || isLinkedToAnyPO}
                              className="h-4 w-4 text-[#6A89A7] rounded"
                            />
                          </td>
                          <td className="p-3">
                            <div className="flex items-center">
                              {item.SalesItemCode}
                              {isAlreadyAdded && (
                                <span className="ml-2 text-xs bg-gray-500 text-white px-2 py-1 rounded">Added</span>
                              )}
                              {/* NEW: Show badge for items linked to other POs */}
                              {isLinkedToAnyPO && !isAlreadyAdded && (
                                <span className="ml-2 text-xs bg-red-500 text-white px-2 py-1 rounded">Linked to PO</span>
                              )}
                            </div>
                          </td>
                          <td className="p-3">{item.ProductName}</td>
                          <td className="p-3">{item.Description}</td>
                          <td className="p-3">
                            <div>
                              <div className="font-medium">{item.SalesOrderNumber}</div>
                              <div className="text-xs text-gray-500">
                                {new Date(item.SalesOrderDate).toLocaleDateString()}
                              </div>
                            </div>
                          </td>
                          <td className="p-3">{item.CustomerCode}</td>
                          <td className="p-3">{item.UOMID}</td>
                          <td className="p-3 text-right">{item.Quantity}</td>
                          <td className="p-3 text-right">{parseFloat(item.UnitPrice).toFixed(2)}</td>
                          <td className="p-3 text-right">{parseFloat(item.Total).toFixed(2)}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="10" className="p-4 text-center text-gray-500">
                        {searchTerm ? 'No items match your search' : 'No sales items found'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
        <div className="p-6 border-t flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {selectedItems.length} item(s) selected
          </div>
          <div className="flex gap-3">
            <button 
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-[#64748b] font-semibold hover:bg-[#F1F5F9] transition-colors"
            >
              Cancel
            </button>
            <button 
              onClick={handleAddSelectedItems}
              disabled={selectedItems.length === 0}
              className="px-4 py-2 bg-[#6A89A7] text-white rounded-lg hover:bg-[#88BDF2] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              <PlusIcon className="mr-1" />
              Add Selected Items to PO
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PurchaseOrderFormModal = ({ open, onClose, onSubmit, initialData, isEdit }) => {
  const [header, setHeader] = useState(initialHeader);
  // Changed initial state to empty array
  const [items, setItems] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dropdownData, setDropdownData] = useState({
    vendors: [],
    currencies: [],
    locations: [],
    taxCodes: [],
    products: [],
    statusOptions: [],
    branches: [],
    nextPONumber: '',
    nextItemCode: ''
  });
  
  // Added state for user management
  const [currentUser, setCurrentUser] = useState(null);
  const [isUserValid, setIsUserValid] = useState(false);
  const [authError, setAuthError] = useState(null);
  
  // Added state for vendor addresses
  const [vendorAddresses, setVendorAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  
  // Added state for vendor contacts
  const [vendorContacts, setVendorContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  
  // Added state for branches loading and data
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [branches, setBranches] = useState([]);
  
  // Added state for linked documents
  const [linkedDocumentType, setLinkedDocumentType] = useState('');
  const [showSalesItemsModal, setShowSalesItemsModal] = useState(false);
  
  // Department-related state
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [screenConfig, setScreenConfig] = useState(null);
  const [showDepartmentField, setShowDepartmentField] = useState(false);
  const [showDepartmentItemField, setShowDepartmentItemField] = useState(false);
  
  const [isInitialized, setIsInitialized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  
  // Add state for product multi-select modal
  const [showProductMultiSelectModal, setShowProductMultiSelectModal] = useState(false);
  const [savedItems, setSavedItems] = useState([]);
  
  // Function to toggle maximize/minimize
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };
  
  // Function to fetch current user
  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setIsUserValid(false);
        setAuthError('No token found. Please log in again.');
        return false;
      }
      const userRes = await fetch(`${API_URL}/current-user`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const userData = await userRes.json();
      
      if (userData.user && userData.user.UserID) {
        setCurrentUser(userData.user);
        setIsUserValid(true);
        setAuthError(null);
        console.log('Current user fetched:', userData);
        return true;
      } else {
        console.error('Invalid user data received:', userData);
        setIsUserValid(false);
        setAuthError('User information not available. Please log in again.');
        return false;
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
      setIsUserValid(false);
      setAuthError('Error fetching user information. Please refresh the page.');
      return false;
    }
  };
  
  // Function to fetch branches
  const fetchBranches = async () => {
    setLoadingBranches(true);
    try {
      const response = await fetch(`${API_URL}/branches`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setBranches(data);
      setDropdownData(prev => ({ ...prev, branches: data }));
      return data;
    } catch (error) {
      console.error('Error fetching branches:', error);
      const defaultBranches = [
        { BranchCode: 'BR001', BranchName: 'Main Branch' },
        { BranchCode: 'BR002', BranchName: 'North Branch' },
        { BranchCode: 'BR003', BranchName: 'South Branch' }
      ];
      setBranches(defaultBranches);
      setDropdownData(prev => ({ ...prev, branches: defaultBranches }));
      return defaultBranches;
    } finally {
      setLoadingBranches(false);
    }
  };
  
  // Function to fetch departments
  const fetchDepartments = async () => {
    setLoadingDepartments(true);
    try {
      const response = await fetch(`${API_URL}/departments`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      const data = await response.json();
      console.log('Fetched departments:', data);
      setDepartments(data);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };
  
  // Function to fetch screen configuration
  const fetchScreenConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/screens`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      const data = await response.json();
      console.log('Fetched screen configurations:', data);
      
      // Find the purchase order screen configuration
      const poScreen = data.screens.find(screen => 
        screen.ScreenName.toLowerCase() === 'PurchaseOrder'.toLowerCase()
      );
      
      console.log('Purchase order screen configuration:', poScreen);
      if (poScreen) {
        setScreenConfig(poScreen);
        // Show department field if ShowOnHeader is true
        const shouldShowDepartment = poScreen.ShowOnHeader === true;
        setShowDepartmentField(shouldShowDepartment);
        
        // Show department item field if ShowOnItem is true
        const shouldShowDepartmentItem = poScreen.ShowOnItem === true;
        setShowDepartmentItemField(shouldShowDepartmentItem);
        
        // If department fields should be shown, fetch departments
        if (shouldShowDepartment || shouldShowDepartmentItem) {
          fetchDepartments();
        }
      }
    } catch (error) {
      console.error('Error fetching screen configuration:', error);
      setShowDepartmentField(false);
      setShowDepartmentItemField(false);
    }
  };
  
  // Function to fetch purchase order details for editing
  const fetchPurchaseOrderDetails = async (poNumber) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/purchase-order/${poNumber}`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('Purchase order details fetched:', data);
      return data;
    } catch (error) {
      console.error('Error fetching purchase order details:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };


  // Replace the fetchDropdownData function with these separate functions:

const fetchVendors = async () => {
  try {
    const response = await fetch(`${API_URL}/vendors`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching vendors:', error);
    return [];
  }
};

const fetchCurrencies = async () => {
  try {
    const response = await fetch(`${API_URL}/currencies`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    return data.currencies || [];
  } catch (error) {
    console.error('Error fetching currencies:', error);
    return [];
  }
};

const fetchLocations = async () => {
  try {
    const response = await fetch(`${API_URL}/locations`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching locations:', error);
    return [];
  }
};

const fetchTaxes = async () => {
  try {
    const response = await fetch(`${API_URL}/taxes`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching taxes:', error);
    return [];
  }
};

const fetchProducts = async () => {
  try {
    const response = await fetch(`${API_URL}/products`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching products:', error);
    return [];
  }
};

const fetchNextPONumber = async () => {
  try {
    const response = await fetch(`${API_URL}/purchase-order/next-po-number`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    return data.nextPONumber || '';
  } catch (error) {
    console.error('Error fetching next PO number:', error);
    return '';
  }
};

const fetchNextItemCode = async () => {
  try {
    const response = await fetch(`${API_URL}/purchase-order/next-item-code`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    return data.nextItemCode || '';
  } catch (error) {
    console.error('Error fetching next item code:', error);
    return '';
  }
};

const fetchStatusOptions = async () => {
  // Status options are static, so we can just return them
  return STATUS_OPTIONS;
};

const fetchVendorAddresses = async (vendorCode) => {
  try {
    const response = await fetch(`${API_URL}/vendors/${vendorCode}/addresses`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching vendor addresses:', error);
    return [];
  }
};

const fetchVendorContacts = async (vendorCode) => {
  try {
    const response = await fetch(`${API_URL}/vendors/${vendorCode}/contacts`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching vendor contacts:', error);
    return [];
  }
};

// Replace the fetchDropdownData function with this new function:
const fetchAllDropdownData = async () => {
  try {
    setLoading(true);
    
    // Fetch all data in parallel
    const [
      vendors,
      currencies,
      locations,
      taxes,
      products,
      nextPONumber,
      nextItemCode,
      statusOptions
    ] = await Promise.all([
      fetchVendors(),
      fetchCurrencies(),
      fetchLocations(),
      fetchTaxes(),
      fetchProducts(),
      fetchNextPONumber(),
      fetchNextItemCode(),
      fetchStatusOptions()
    ]);
    
    // Update the dropdown data state
    setDropdownData({
      vendors,
      currencies,
      locations,
      taxCodes: taxes,
      products,
      statusOptions,
      branches, // branches are already fetched in fetchBranches
      nextPONumber,
      nextItemCode
    });
    
    // Set PO number if creating new
    if (!isEdit && nextPONumber) {
      setHeader(prev => ({ 
        ...prev, 
        poNumber: nextPONumber,
        is_active: '0' // Default to Approved
      }));
    }
  } catch (error) {
    console.error('Error fetching dropdown data:', error);
    alert(`Error loading form data: ${error.message}`);
  } finally {
    setLoading(false);
  }
};
  
  // ERP Command: Save popup items to table
  const executeERPCommand = async (command, data) => {
    try {
      const response = await fetch(`${API_URL}/erp/execute-command`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          command,
          data
        })
      });
      
      if (!response.ok) {
        throw new Error(`ERP command failed: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error executing ERP command ${command}:`, error);
      throw error;
    }
  };
  
  // Save selected products to table
  const savePopupItemsToTable = async (selectedProductCodes) => {
    const productsToSave = selectedProductCodes.map(productCode => {
      const product = dropdownData.products.find(p => p.ProductCode === productCode);
      return {
        productCode: product.ProductCode,
        productName: product.ProductName,
        uom: product.UOMName || product.UOMID || '',
        savedDate: new Date().toISOString()
      };
    });
    
    return await executeERPCommand(ERP_COMMANDS.SAVE_POPUP_ITEMS, {
      poNumber: header.poNumber,
      items: productsToSave
    });
  };
  
  // Load saved items from table
  const loadSavedItemsFromTable = async () => {
    try {
      const result = await executeERPCommand(ERP_COMMANDS.LOAD_SAVED_ITEMS, {
        poNumber: header.poNumber
      });
      
      if (result && result.items && result.items.length > 0) {
        setSavedItems(result.items);
        
        // Convert saved items to form items
        const formItems = result.items.map(savedItem => ({
          ...initialItem,
          productCode: savedItem.productCode,
          productName: savedItem.productName,
          uom: savedItem.uom,
          savedToTable: true
        }));
        
        setItems(formItems);
      }
    } catch (error) {
      console.error('Error loading saved items:', error);
    }
  };
  
  // Delete saved items from table
  const deleteSavedItemsFromTable = async () => {
    try {
      await executeERPCommand(ERP_COMMANDS.DELETE_SAVED_ITEMS, {
        poNumber: header.poNumber
      });
      setSavedItems([]);
    } catch (error) {
      console.error('Error deleting saved items:', error);
    }
  };
  
  // Autofill selected products into the table
  const autofillSelectedProducts = (selectedProductCodes) => {
    if (selectedProductCodes.length === 0) return;
    
    try {
      const newItems = selectedProductCodes.map(productCode => {
        const product = dropdownData.products.find(p => p.ProductCode === productCode);
        if (!product) return { ...initialItem };
        
        return {
          ...initialItem,
          productCode: product.ProductCode,
          productName: product.ProductName,
          uom: product.UOMName || '',
          description: product.Description || '',
          department_id: showDepartmentItemField ? "" : undefined,
        };
      });
      
      // Append new items to existing items instead of replacing them
      setItems(prevItems => [...prevItems, ...newItems]);
      
      // Return success for the modal to show notification
      return { success: true, count: selectedProductCodes.length };
    } catch (error) {
      console.error('Error in autofillSelectedProducts:', error);
      // Return error for the modal to show error message
      return { success: false, error: error.message };
    }
  };
  
  // Handle multi-select products
  const handleMultiSelectProducts = (selectedProductCodes) => {
    if (selectedProductCodes.length === 0) return;
    
    const newItems = selectedProductCodes.map(productCode => {
      const product = dropdownData.products.find(p => p.ProductCode === productCode);
      if (!product) return { ...initialItem };
      
      return {
        ...initialItem,
        productCode: product.ProductCode,
        productName: product.ProductName,
        uom: product.UOMName || '',
        description: product.Description || '',
        department_id: showDepartmentItemField ? "" : undefined,
      };
    });
    
    // Append new items to existing items instead of replacing them
    setItems(prevItems => [...prevItems, ...newItems]);
  };
  
  // Function to calculate tax for an item
// Function to calculate tax for an item
const calculateItemTax = (item) => {
  const quantity = parseFloat(item.quantity) || 0;
  const unitPrice = parseFloat(item.unitPrice) || 0;
  const discount = parseFloat(item.discount) || 0;
  
  // Calculate item subtotal before discount
  const itemSubtotal = quantity * unitPrice;
  const discountAmount = (discount / 100) * itemSubtotal;
  
  // Calculate tax for this item
  let taxAmount = 0;
  let taxRate = 0;
  
  // Debug logging
  console.log('Calculating tax for item:', item);
  console.log('Available tax codes:', dropdownData.taxCodes);
  
  // Ensure taxCodes array exists and has items
  if (item.taxCode && dropdownData.taxCodes && dropdownData.taxCodes.length > 0) {
    const taxCode = dropdownData.taxCodes.find(t => t.TaxCode === item.taxCode);
    console.log('Found tax code:', taxCode);
    
    if (taxCode) {
      // Try different possible field names for tax rate
      taxRate = parseFloat(taxCode.TaxPercentage || taxCode.Rate || taxCode.taxRate || taxCode.Percentage || 0);
      console.log('Tax rate:', taxRate);
      
      // Apply tax on amount after discount
      const taxableAmount = itemSubtotal - discountAmount;
      taxAmount = taxableAmount * (taxRate / 100);
      console.log('Taxable amount:', taxableAmount, 'Tax amount:', taxAmount);
    }
  }
  
  // Calculate item total (subtotal - discount + tax)
  const total = itemSubtotal - discountAmount + taxAmount;
  console.log('Item total:', total);
  
  return {
    total: total.toFixed(2),
    taxAmount: taxAmount.toFixed(2),
    taxRate: taxRate.toFixed(2) // Format to 2 decimal places
  };
};
  
  // Function to recalculate all items when tax codes change
 // Function to recalculate all items when tax codes change
const recalculateAllItems = () => {
  setItems(prevItems => {
    return prevItems.map(item => {
      const { total, taxAmount, taxRate } = calculateItemTax(item);
      return {
        ...item,
        total,
        taxAmount,
        taxRate: taxRate.toString() // Ensure taxRate is stored as a string
      };
    });
  });
};
  
  // Recalculate items when dropdown data (tax codes) changes
  useEffect(() => {
    if (dropdownData.taxCodes.length > 0 && items.length > 0) {
      recalculateAllItems();
    }
  }, [dropdownData.taxCodes, items.length]);
  
  // Calculate summary values
 // Calculate summary values
const summary = useMemo(() => {
  let subtotal = 0;
  let totalDiscount = 0;
  let totalTax = 0;
  
  items.forEach(item => {
    const quantity = parseFloat(item.quantity) || 0;
    const unitPrice = parseFloat(item.unitPrice) || 0;
    const discount = parseFloat(item.discount) || 0;
    
    // Calculate item total before discount
    const itemSubtotal = quantity * unitPrice;
    const discountAmount = (discount / 100) * itemSubtotal;
    subtotal += itemSubtotal;
    totalDiscount += discountAmount;
    
    // Calculate tax for this item
    if (item.taxCode && dropdownData.taxCodes && dropdownData.taxCodes.length > 0) {
      const taxCode = dropdownData.taxCodes.find(t => t.TaxCode === item.taxCode);
      if (taxCode) {
        // Try different possible field names for tax rate
        const taxRate = parseFloat(taxCode.TaxPercentage || taxCode.Rate || taxCode.taxRate || taxCode.Percentage || 0);
        // Apply tax on amount after discount
        const taxableAmount = itemSubtotal - discountAmount;
        const taxAmount = taxableAmount * (taxRate / 100);
        totalTax += taxAmount;
      }
    }
  });
  
  const grandTotal = subtotal - totalDiscount + totalTax;
  
  return {
    subtotal: subtotal.toFixed(2),
    totalDiscount: totalDiscount.toFixed(2),
    totalTax: totalTax.toFixed(2),
    grandTotal: grandTotal.toFixed(2)
  };
}, [items, dropdownData.taxCodes]);
  
  // Update header when summary changes
  useEffect(() => {
    setHeader(prev => ({
      ...prev,
      SubTotal: summary.subtotal,
      TotalDiscount: summary.totalDiscount,
      TotalTax: summary.totalTax,
      TotalAmount: summary.grandTotal
    }));
  }, [summary]);
  
  // Combined effect to handle vendor, address and contact changes
 // Update the useEffect that handles vendor selection
useEffect(() => {
  console.log('Vendor code changed:', header.vendorCode);
  if (header.vendorCode) {
    const vendor = dropdownData.vendors.find(v => v.vendor_code === header.vendorCode);
    console.log('Found vendor:', vendor);
    
    if (vendor) {
      // Fetch vendor addresses and contacts
      const fetchVendorDetails = async () => {
        try {
          const [addresses, contacts] = await Promise.all([
            fetchVendorAddresses(header.vendorCode),
            fetchVendorContacts(header.vendorCode)
          ]);
          
          console.log('Fetched addresses:', addresses);
          console.log('Fetched contacts:', contacts);
          console.log('Header vendorContactId:', header.vendorContactId);
          
          setVendorAddresses(addresses);
          setVendorContacts(contacts);
          
          // If editing and we have a vendorContactId, find the contact
          if (isEdit && header.vendorContactId && !isInitialized) {
            // Convert vendorContactId to number for comparison
            const contactId = parseInt(header.vendorContactId);
            console.log('Looking for contact with id:', contactId);
            
            const contact = contacts.find(c => c.contact_id === contactId);
            console.log('Found contact for edit:', contact);
            
            if (contact) {
              console.log('Setting selected contact for edit:', contact);
              setSelectedContact(contact);
            } else {
              console.log('No contact found with id:', contactId);
              setSelectedContact(null);
            }
          } else if (!isEdit && !isInitialized) {
            // Reset selected contact when vendor changes for new PO
            setSelectedContact(null);
            setHeader(prev => ({ ...prev, vendorContactId: '' }));
          }
        } catch (error) {
          console.error('Error fetching vendor details:', error);
          setVendorAddresses([]);
          setVendorContacts([]);
        }
      };
      
      fetchVendorDetails();
    }
  } else {
    console.log('No vendor selected');
    setVendorAddresses([]);
    setSelectedAddress(null);
    setVendorContacts([]);
    setSelectedContact(null);
    if (!isInitialized) {
      setHeader(prev => ({ 
        ...prev, 
        vendorAddressId: '',
        vendorContactId: ''
      }));
    }
  }
  
  // Mark as initialized after first run
  if (!isInitialized) {
    setIsInitialized(true);
  }
}, [header.vendorCode, dropdownData.vendors, isEdit, header.vendorContactId, isInitialized]);
  
  // Update selected address when vendorAddressId changes (for user selections)
  useEffect(() => {
    if (isInitialized && header.vendorAddressId && vendorAddresses.length > 0) {
      const address = vendorAddresses.find(addr => addr.address_id === parseInt(header.vendorAddressId));
      console.log('Found address for display:', address);
      setSelectedAddress(address);
    } else if (isInitialized && !header.vendorAddressId) {
      setSelectedAddress(null);
    }
  }, [header.vendorAddressId, vendorAddresses, isInitialized]);
  
  // Update selected contact when vendorContactId changes (for user selections)
 useEffect(() => {
  console.log('Checking for selected contact:', {
    isInitialized,
    vendorContactId: header.vendorContactId,
    vendorContactsLength: vendorContacts.length
  });
  
  if (isInitialized && header.vendorContactId && vendorContacts.length > 0) {
    // Convert vendorContactId to number for comparison
    const contactId = parseInt(header.vendorContactId);
    console.log('Looking for contact with id:', contactId);
    
    const contact = vendorContacts.find(c => c.contact_id === contactId);
    console.log('Found contact for display:', contact);
    
    setSelectedContact(contact);
  } else if (isInitialized && !header.vendorContactId) {
    console.log('No vendorContactId, setting selected contact to null');
    setSelectedContact(null);
  }
}, [header.vendorContactId, vendorContacts, isInitialized]);
  
  // Handle keyboard shortcuts for multi-select modals
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      // Check for Alt+P - Multi-select products
      if (e.altKey && e.key === 'p') {
        e.preventDefault();
        setShowProductMultiSelectModal(true);
        return;
      }
      
      // Check for Alt+C - Focus vendor dropdown
      if (e.altKey && e.key === 'c') {
        console.log('Alt+C pressed');
        e.preventDefault();
        // Find and focus the vendor dropdown
        const vendorDropdown = document.querySelector('[data-vendor-dropdown]');
        if (vendorDropdown) {
          vendorDropdown.click();
        }
        return;
      }
      
      // Check for Alt+T - Focus tax dropdown
      if (e.altKey && e.key === 't') {
        e.preventDefault();
        // Find and focus the first tax dropdown
        const taxDropdown = document.querySelector('[data-tax-dropdown]');
        if (taxDropdown) {
          taxDropdown.click();
        }
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);
  
  // UPDATED useEffect to handle initialization with proper async handling
  useEffect(() => {
    if (open) {
      const initializeForm = async () => {
        setIsInitialized(false);
        setIsMaximized(false);
        
        // Fetch current user, branches, and screen config in parallel
        const [userValid, branchesData] = await Promise.all([
          fetchCurrentUser(),
          fetchBranches(),
          fetchScreenConfig()
        ]);
        
        if (userValid) {
          if (isEdit && initialData) {
            try {
              // Fetch the complete purchase order details from the API
              const poDetails = await fetchPurchaseOrderDetails(initialData.PurchaseOrderNumber || initialData.purchaseOrderNumber);
              
               const departmentId = initialData.DepartmentID || initialData.department_id || '';
      console.log('Setting department_id to:', departmentId);
              // Normalize payment term to match one of the options
              const paymentTerm = poDetails.PaymentTerm || '';
              const normalizedPaymentTerm = PAYMENT_TERMS_OPTIONS.find(
                option => option.value && option.value.toLowerCase() === paymentTerm.toLowerCase()
              )?.value || '';
              
              // Ensure we have the branch data before setting the form
              const BranchCode = poDetails.BranchCode || '';
              const branchName = poDetails.BranchName || '';
              
              // If we have a branch code but it's not in the branches list, add it temporarily
              if (BranchCode && !branchesData.some(b => b.BranchCode === BranchCode)) {
                // Create a temporary branch object using the BranchName from the API response
                const tempBranch = {
                  BranchCode: BranchCode,
                  BranchName: branchName || 'Unknown Branch'
                };
                
                // Add it to the branches list
                const updatedBranches = [...branchesData, tempBranch];
                setBranches(updatedBranches);
                setDropdownData(prev => ({ ...prev, branches: updatedBranches }));
              }
              
              // FIXED: Convert vendor address ID to string if it's a number
              const vendorAddressId = poDetails.VendorAddressID ? 
                (typeof poDetails.VendorAddressID === 'number' ? poDetails.VendorAddressID.toString() : poDetails.VendorAddressID) : '';
              
              // FIXED: Convert vendor contact ID to string if it's a number
             let vendorContactId = '';
if (poDetails.VendorContactID !== undefined && poDetails.VendorContactID !== null) {
  if (Array.isArray(poDetails.VendorContactID)) {
    // Take the first element of the array
    vendorContactId = poDetails.VendorContactID[0].toString();
  } else {
    vendorContactId = poDetails.VendorContactID.toString();
  }
}
              
              // FIXED: Handle LinkedDocumentType that might be an array
              const linkedDocType = Array.isArray(poDetails.LinkedDocumentType) 
                ? poDetails.LinkedDocumentType[0] 
                : poDetails.LinkedDocumentType || '';

                // FIXED: Handle is_active field properly when it's an array
let isActive = '1'; // Default to Approved
if (poDetails.is_active !== undefined && poDetails.is_active !== null) {
  if (Array.isArray(poDetails.is_active)) {
    // Take the first element of the array
    const firstValue = poDetails.is_active[0];
    if (typeof firstValue === 'boolean') {
      isActive = firstValue ? '1' : '0';
    } else {
      isActive = firstValue.toString();
    }
  } else if (typeof poDetails.is_active === 'boolean') {
    isActive = poDetails.is_active ? '1' : '0';
  } else {
    isActive = poDetails.is_active.toString();
  }
}

              
              // Set header data
              setHeader({
                poNumber: poDetails.PurchaseOrderNumber || '',
                poDate: poDetails.PurchaseOrderDate ? new Date(poDetails.PurchaseOrderDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                vendorCode: poDetails.VendorCode || '',
                vendorAddressId: vendorAddressId,
                vendorContactId: vendorContactId,
                currencyCode: poDetails.CurrencyCode || '',
                deliveryDate: poDetails.DeliveryDate ? new Date(poDetails.DeliveryDate).toISOString().split('T')[0] : '',
                paymentTerms: normalizedPaymentTerm,
                remarks: poDetails.Remarks || '',
                locationCode: poDetails.LocationCode || '',
                BranchCode: BranchCode,
                is_active:  isActive,
                SubTotal: poDetails.SubTotal?.toString() || '0',
                TotalDiscount: poDetails.TotalDiscount?.toString() || '0',
                TotalTax: poDetails.TotalTax?.toString() || '0',
                TotalAmount: poDetails.TotalAmount?.toString() || '0',
                LinkedDocumentType: linkedDocType,
                LinkedDocumentNumber: poDetails.LinkedDocumentNumber || '',
                department_id: departmentId.toString() || '' // Added department_id
              });
              
              // Set linked document type state for the dropdown
              if (linkedDocType) {
                setLinkedDocumentType(linkedDocType);
              }
              
              // Set items data
              if (poDetails.Items && poDetails.Items.length > 0) {
                setItems(poDetails.Items.map(item => {
                  const quantity = parseFloat(item.Quantity) || 0;
                  const remainingQty = item.RemainingQty !== undefined && item.RemainingQty !== null 
                    ? item.RemainingQty 
                    : quantity;
                  
                  // FIXED: Handle LinkedDocumentType that might be an array
                  const itemLinkedDocType = Array.isArray(item.LinkedDocumentType) 
                    ? item.LinkedDocumentType[0] 
                    : item.LinkedDocumentType || '';
                  
                  return {
                    itemCode: item.ItemCode || '',
                    productCode: item.ProductCode || '',
                    productName: item.ProductName || '',
                    description: item.Description || '',
                    uom: item.UOMName || '',
                    quantity: item.Quantity ? item.Quantity.toString() : '',
                    remainingQuantity: remainingQty.toString(),
                    mrnReceivedQty: item.MRNQuantity ? item.MRNQuantity.toString() : '0',
                    unitPrice: item.UnitPrice ? item.UnitPrice.toString() : '',
                    discount: item.Discount ? item.Discount.toString() : '0',
                    taxCode: item.TaxCode || '',
                    total: item.Total ? item.Total.toString() : '0',
                    taxAmount: item.TaxAmount ? item.TaxAmount.toString() : '0',
                    LinkedDocumentType: itemLinkedDocType,
                    LinkedDocumentNumber: item.LinkedDocumentNumber || '',
                    LinkedItemCode: item.LinkedItemCode || '',
                    isLinked: !!item.LinkedItemCode, // NEW: Set isLinked flag
                    department_id: item.DepartmentID?.toString() || '' // Added department_id
                  };
                }));
              } else {
                setItems([]);
              }
            } catch (error) {
              console.error('Error initializing form with purchase order details:', error);
              alert('Error loading purchase order details. Please try again.');
              onClose();
            }
          } else {
            // For new PO, set initial header with current date but empty PO number
            setHeader({
              ...initialHeader,
              poDate: new Date().toISOString().split('T')[0]
            });
            // Start with empty items array
            setItems([]);
            setSelectedAddress(null);
            setSelectedContact(null);
          }
          setErrors({});
          setIsSubmitting(false);
          
          // Fetch dropdown data after initializing the form
          // await fetchDropdownData();
          await fetchAllDropdownData();
          
          // Load saved items from table for new records
          if (!isEdit) {
            loadSavedItemsFromTable();
          }
        }
      };
      
      initializeForm();
    }
  }, [open, initialData, isEdit]);
  
  // const fetchDropdownData = async () => {
  //   try {
  //     setLoading(true);
  //     const response = await fetch(`${API_URL}/purchase-order/form-data`);
      
  //     if (!response.ok) {
  //       const errorData = await response.json();
  //       throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  //     }
      
  //     const data = await response.json();
  //     console.log('Dropdown data fetched:', data);
  //     setDropdownData({
  //       vendors: data.vendors || [],
  //       currencies: data.currencies || [],
  //       locations: data.locations || [],
  //       taxCodes: data.taxCodes || [],
  //       products: data.products || [],
  //       statusOptions: data.statusOptions || [],
  //       branches: data.branches || branches,
  //       nextPONumber: data.nextPONumber || '',
  //       nextItemCode: data.nextItemCode || ''
  //     });
      
  //     // Update the branches state as well
  //     if (data.branches && data.branches.length > 0) {
  //       setBranches(data.branches);
  //     }
      
  //     // Set PO number if creating new - this is the key fix
  //     if (!isEdit && data.nextPONumber) {
  //       setHeader(prev => ({ 
  //         ...prev, 
  //         poNumber: data.nextPONumber,
  //         is_active: '0' // Default to Approved
  //       }));
  //     }
  //   } catch (error) {
  //     console.error('Error fetching dropdown data:', error);
  //     alert(`Error loading form data: ${error.message}`);
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  
  const handleHeaderChange = (e) => {
    const { name, value } = e.target;
    setHeader({ ...header, [name]: value });
  };
  
  const handleVendorAddressChange = (e) => {
    const addressId = e.target.value;
    console.log('Address selected:', addressId);
    console.log('Available addresses:', vendorAddresses);
    
    setHeader(prev => ({ ...prev, vendorAddressId: addressId }));
    
    if (addressId) {
      const address = vendorAddresses.find(addr => addr.address_id === parseInt(addressId));
      console.log('Found address object:', address);
      setSelectedAddress(address);
    } else {
      setSelectedAddress(null);
    }
  };
  
  const handleVendorContactChange = (e) => {
    const contactId = e.target.value;
    console.log('Contact selected:', contactId);
    console.log('Available contacts:', vendorContacts);
    
    setHeader(prev => ({ ...prev, vendorContactId: contactId }));
    
    if (contactId) {
      const contact = vendorContacts.find(c => c.contact_id === parseInt(contactId));
      console.log('Found contact object:', contact);
      setSelectedContact(contact);
    } else {
      setSelectedContact(null);
    }
  };
  
const handleItemChange = (idx, e) => {
  const { name, value } = e.target;
  const newItems = [...items];
  newItems[idx][name] = value;
  console.log("Item change:", name, value, newItems[idx]);
  
  // Calculate total when quantity, unit price, discount, or tax code changes
  if (name === 'quantity' || name === 'unitPrice' || name === 'discount' || name === 'taxCode') {
    const { total, taxAmount, taxRate } = calculateItemTax(newItems[idx]);
    newItems[idx].total = total;
    newItems[idx].taxAmount = taxAmount;
    newItems[idx].taxRate = taxRate; // Update tax rate
    
    // Update remaining quantity when quantity changes
    if (name === 'quantity') {
      const quantity = parseFloat(value) || 0;
      const mrnQty = parseFloat(newItems[idx].mrnReceivedQty) || 0;
      const remaining = quantity - mrnQty;
      newItems[idx].remainingQuantity = remaining >= 0 ? remaining.toString() : '0';
    }
  }
  
  setItems(newItems);
};
  
  // Handle product selection - UPDATED to auto-fill UOM and Product Code
 const handleProductChange = (idx, productCode) => {
  const newItems = [...items];
  
  if (productCode) {
    const selectedProduct = dropdownData.products.find(p => p.ProductCode === productCode);
    if (selectedProduct) {
      // Auto-fill all product-related fields
      newItems[idx] = {
        ...newItems[idx],
        productCode: selectedProduct.ProductCode,
        productName: selectedProduct.ProductName,
        uom: selectedProduct.UOMName || '',
        description: selectedProduct.Description || ''
      };
      
      // Recalculate totals after product change
      const { total, taxAmount, taxRate } = calculateItemTax(newItems[idx]);
      newItems[idx].total = total;
      newItems[idx].taxAmount = taxAmount;
      newItems[idx].taxRate = taxRate; // Update tax rate
    }
  } else {
    // Clear product-related fields if no product is selected
    newItems[idx] = {
      ...newItems[idx],
      productCode: '',
      productName: '',
      uom: '',
      description: ''
    };
  }
  
  setItems(newItems);
};
  
  // Handle linked document type change - FIXED
  const handleLinkedDocumentTypeChange = (e) => {
    const value = e.target.value;
    // Update both states simultaneously
    setHeader(prev => ({ 
      ...prev, 
      LinkedDocumentType: value,
      LinkedDocumentNumber: '' // Reset linked document number when type changes
    }));
    console.log("Linked Document Type changed to:", value);
    setLinkedDocumentType(value);
  };
  
  // Handle items selected from linked document - UPDATED
  const handleLinkedItemsSelected = (selectedItems) => {
    console.log("Selected items from modal:", selectedItems);
    console.log("Current dropdown data:", dropdownData);
    
    // Create new items from selected sales items
    const newItems = selectedItems.map((salesItem, index) => {
      const quantity = parseFloat(salesItem.Quantity) || 0;
      const unitPrice = parseFloat(salesItem.UnitPrice) || 0;
      
      // Find matching product in dropdown data
      const matchingProduct = dropdownData.products.find(
        p => p.ProductCode === salesItem.ProductCode
      );
      
      // Find default tax code (first available)
      const defaultTaxCode = dropdownData.taxCodes.length > 0 
        ? dropdownData.taxCodes[0].TaxCode 
        : '';
      
      // Generate unique item code - FIXED
      let itemCode;
      if (dropdownData.nextItemCode) {
        // Parse the nextItemCode to extract the number part
        const nextItemCodeMatch = dropdownData.nextItemCode.match(/ITEM-(\d+)/);
        if (nextItemCodeMatch) {
          const nextNumber = parseInt(nextItemCodeMatch[1]) + index;
          itemCode = `ITEM-${String(nextNumber).padStart(3, '0')}`;
        } else {
          // Fallback if nextItemCode format is unexpected
          itemCode = `ITEM-${Date.now()}-${index}`;
        }
      } else {
        // Fallback if nextItemCode is not available
        itemCode = `ITEM-${Date.now()}-${index}`;
      }
      
      // Create new item with all required fields
      const newItem = {
        ...initialItem,
        itemCode,
        productCode: salesItem.ProductCode || '',
        productName: matchingProduct?.ProductName || salesItem.ProductName || '',
        description: matchingProduct?.Description || salesItem.Description || '',
        uom: matchingProduct?.UOMID || salesItem.UOMID || '',
        quantity: quantity.toString(),
        unitPrice: unitPrice.toString(),
        discount: '0', // Default to no discount
        taxCode: defaultTaxCode, // IMPORTANT: Set default tax code
        total: '0', // Will be calculated
        taxAmount: '0', // Will be calculated
        remainingQuantity: quantity.toString(),
        mrnReceivedQty: '0',
        LinkedDocumentType: 'SO',
        LinkedDocumentNumber: salesItem.SalesOrderNumber,
        LinkedItemCode: salesItem.SalesItemCode,
        isLinked: true, // NEW: Mark as linked item
        department_id: '' // Initialize empty department_id
      };
      console.log("New item created from linked item:", newItem);
      
      // Calculate totals immediately
      const { total, taxAmount, taxRate } = calculateItemTax(newItem);
      newItem.total = total;
      newItem.taxAmount = taxAmount;
      newItem.taxRate = taxRate.toString();
      newItem.taxRate = taxRate; // Add this line
      
      return newItem;
    });
    
    // Update state with new items
    setItems(prevItems => {
      const updatedItems = [...prevItems, ...newItems];
      console.log("Updated items after adding:", updatedItems);
      return updatedItems;
    });
    
    // Update header with linked document info
    if (selectedItems.length > 0) {
      setHeader(prevHeader => ({
        ...prevHeader,
        LinkedDocumentType: prevHeader.LinkedDocumentType || 'SO',
        LinkedDocumentNumber: prevHeader.LinkedDocumentNumber || selectedItems[0].SalesOrderNumber
      }));
    }
    
    // Force recalculation after state updates
    setTimeout(() => {
      recalculateAllItems();
    }, 100);
  };
  
  const addItem = () => {
    setItems([...items, { 
      ...initialItem,
      LinkedDocumentType: linkedDocumentType,
      LinkedDocumentNumber: header.LinkedDocumentNumber,
      isLinked: false, // NEW: Mark as manually added item
      department_id: showDepartmentItemField ? '' : undefined // Only include if department field is shown
    }]);
  };
  
  const removeItem = (idx) => {
    // Allow removing all items
    setItems(items.filter((_, i) => i !== idx));
  };
  
  const validate = () => {
    const errs = {};
    if (!header.poNumber || !header.poNumber.trim()) errs.poNumber = 'Required';
    if (!header.poDate) errs.poDate = 'Required';
    if (!header.vendorCode) errs.vendorCode = 'Required';
    if (!header.currencyCode) errs.currencyCode = 'Required';
    if (!header.locationCode) errs.locationCode = 'Required';
    if (!header.BranchCode) errs.BranchCode = 'Required';
    
    // Validate department field only if it's shown
    if (showDepartmentField && !header.department_id) errs.department_id = 'Required';
    
    // Only validate items if there are any
    if (items.length > 0 && items.some(item => item.productName || item.productCode)) {
      items.forEach((item, idx) => {
        if (!item.productName) errs[`productName_${idx}`] = 'Required';
        if (!item.description) errs[`description_${idx}`] = 'Required';
        if (!item.uom) errs[`uom_${idx}`] = 'Required';
        if (!item.quantity) errs[`quantity_${idx}`] = 'Required';
        if (!item.unitPrice) errs[`unitPrice_${idx}`] = 'Required';
        if (!item.taxCode) errs[`taxCode_${idx}`] = 'Required';
        
        // Validate department item field only if it's shown
        if (showDepartmentItemField && !item.department_id) errs[`department_id_${idx}`] = 'Required';
      });
    }
    
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate() || isSubmitting) return;
    
    // Check if we have a valid current user
    if (!isUserValid || !currentUser || !currentUser.UserID) {
      alert(authError || 'User information not available. Please refresh the page and log in again.');
      return;
    }
    
    setIsSubmitting(true);
    setLoading(true);
    
    try {
      const payload = {
        PurchaseOrderNumber: header.poNumber,
        PurchaseOrderDate: header.poDate,
        VendorCode: header.vendorCode,
        VendorAddressID: header.vendorAddressId ? parseInt(header.vendorAddressId) : null,
        VendorContactID: header.vendorContactId ? parseInt(header.vendorContactId) : null,
        CurrencyCode: header.currencyCode,
        DeliveryDate: header.deliveryDate || null,
        PaymentTerm: header.paymentTerms,
        Remarks: header.remarks,
        LocationCode: header.locationCode,
        BranchCode: header.BranchCode,
        is_active: parseInt(header.is_active),
        SubTotal: parseFloat(summary.subtotal),
        TotalDiscount: parseFloat(summary.totalDiscount),
        TotalTax: parseFloat(summary.totalTax),
        TotalAmount: parseFloat(summary.grandTotal),
        LinkedDocumentType: header.LinkedDocumentType || null, // Ensure this is a string or null
        ...(isEdit ? { updated_by: parseInt(currentUser.UserID) } : { created_by: parseInt(currentUser.UserID) }),
        // Only include department_id if showDepartmentField is true
        ...(showDepartmentField && header.department_id ? { department_id: parseInt(header.department_id) } : {}),
        Items: items.map(item => ({
          ItemCode: item.itemCode,
          ProductName: item.productName,
          ProductCode: item.productCode,
          Description: item.description,
          UOM: item.uom,
          Quantity: parseInt(item.quantity) || 0,
          RemainingQty: parseInt(item.remainingQuantity) || 0,
          UnitPrice: parseFloat(item.unitPrice) || 0,
          LocationCode: header.locationCode,
          Discount: parseFloat(item.discount) || 0,
          TaxCode: item.taxCode,
          Total: parseFloat(item.total) || 0,
          Action: isEdit ? 'Update' : 'Add',
          LinkedDocumentType: item.LinkedDocumentType || null, // Ensure this is a string or null
          LinkedDocumentNumber: item.LinkedDocumentNumber || null,
          LinkedItemCode: item.LinkedItemCode || null,
          // Only include department_id if showDepartmentItemField is true
          ...(showDepartmentItemField && item.department_id ? { department_id: parseInt(item.department_id) } : {})
        }))
      };
      
      console.log('Submitting payload:', payload);
      
      const url = isEdit 
        ? `${API_URL}/purchase-order/${header.poNumber}`
        : `${API_URL}/purchase-order`;
      
      const method = isEdit ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save purchase order');
      }
      
      const result = await response.json();
      
      // If creating new, show the generated item codes
      if (!isEdit && result.Items) {
        console.log('Generated item codes:', result.Items.map(item => item.ItemCode));
      }
      
      // Delete saved items from table after successful submission
      if (!isEdit) {
        await deleteSavedItemsFromTable();
      }
      
      if (onSubmit) {
        onSubmit();
      }
      
      onClose();
    } catch (err) {
      console.error('Error saving purchase order:', err);
      alert(err.message || 'Error saving purchase order');
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };
  
  if (!open) return null;
  
  // Check if there are any items with data
  const hasItemsWithData = items.some(item => item.productName || item.productCode);
  
  return (
    <>
      <div className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${open ? "opacity-100 visible" : "opacity-0 invisible"}`}>
        <style>
          {`
            .hide-scrollbar {
              -ms-overflow-style: none;
              scrollbar-width: none;
            }
            .hide-scrollbar::-webkit-scrollbar {
              display: none;
            }
            .custom-scrollbar::-webkit-scrollbar {
              height: 8px;
            }
            .custom-scrollbar::-webkit-scrollbar-track {
              background: #f1f5f9;
              border-radius: 10px;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb {
              background: #cbd5e1;
              border-radius: 10px;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover {
              background: #94a3b8;
            }
            .shortcut-key {
              display: inline-block;
              background-color: #f1f5f9;
              color: #334155;
              font-family: monospace;
              font-size: 0.75rem;
              padding: 0.125rem 0.25rem;
              border-radius: 0.25rem;
              border: 1px solid #e2e8f0;
              margin: 0 0.125rem;
            }
          `}
        </style>
        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
        <div className={`bg-white shadow-2xl flex flex-col transition-transform transform-gpu duration-300 ease-in-out ${open ? 'translate-x-0' : 'translate-x-full'} ${isMaximized ? 'w-full h-full max-w-none m-0 rounded-none' : 'w-full max-w-6xl h-full rounded-none'}`}>
          {/* Fixed Header */}
          <div className="flex-shrink-0 p-8 pb-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-[#384959]">{isEdit ? 'Edit Purchase Order' : 'Create Purchase Order'}</h2>
              <div className="flex gap-2">
                <button 
                  className="text-[#A0AEC0] hover:text-[#384959] transition-colors" 
                  onClick={toggleMaximize}
                  title={isMaximized ? "Minimize" : "Maximize"}
                >
                  {isMaximized ? <MinimizeIcon size={24} /> : <MaximizeIcon size={24} />}
                </button>
                <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors" onClick={onClose} title="Close">
                  <CloseIcon size={24} />
                </button>
              </div>
            </div>
            
          </div>
          
          {/* User validation warning */}
          {open && !isUserValid && (
            <div className="mx-8 mb-4 bg-red-50 border-l-4 border-red-500 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">
                    {authError || 'User information not available. Please refresh the page and log in again.'}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Scrollable Content Area */}
          <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
            <form onSubmit={handleSubmit} className="space-y-6 pb-6">
              {/* PO Header */}
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <h3 className="text-lg font-semibold text-[#384959] mb-4">Purchase Order Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">PO Number *</label>
                    <input 
                      name="poNumber" 
                      value={header.poNumber} 
                      onChange={handleHeaderChange} 
                      disabled
                      className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]"
                    />
                    {errors.poNumber && <span className="text-xs text-red-500">{errors.poNumber}</span>}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">PO Date *</label>
                    <input 
                      type="date" 
                      name="poDate" 
                      value={header.poDate} 
                      onChange={handleHeaderChange} 
                      className={`w-full border rounded-lg px-3 py-2 ${errors.poDate ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase`}
                    />
                    {errors.poDate && <span className="text-xs text-red-500">{errors.poDate}</span>}
                  </div>
                  
                  {/* Supplier Information Row - 3 columns */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Supplier Name *</label>
                    <SearchableDropdown
                      options={dropdownData.vendors}
                      value={header.vendorCode}
                      onChange={(vendorCode) => setHeader(prev => ({ ...prev, vendorCode }))}
                      getOptionLabel={(vendor) => vendor.vendor_name}
                      getOptionValue={(vendor) => vendor.vendor_code}
                      getOptionSecondaryLabel={(vendor) => vendor.vendor_code}
                      placeholder="Select Supplier"
                      error={errors.vendorCode}
                      className="data-vendor-dropdown"
                    />
                    {errors.vendorCode && <span className="text-xs text-red-500">{errors.vendorCode}</span>}
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Supplier Address</label>
                    <SearchableDropdown
                      options={vendorAddresses}
                      value={header.vendorAddressId}
                      onChange={(addressId) => setHeader(prev => ({ ...prev, vendorAddressId: addressId }))}
                      getOptionLabel={(address) => `${address.address_code} - ${address.address_line1}, ${address.city}`}
                      getOptionValue={(address) => address.address_id.toString()}
                      getOptionSecondaryLabel={(address) => address.address_code}
                      placeholder="Select Address"
                      disabled={!header.vendorCode || vendorAddresses.length === 0}
                      className="data-address-dropdown"
                    />
                    {vendorAddresses.length === 0 && header.vendorCode && (
                      <p className="text-xs text-yellow-600">No addresses available</p>
                    )}
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Supplier Contact</label>
                    <SearchableDropdown
                      options={vendorContacts}
                      value={header.vendorContactId}
                      onChange={(contactId) => setHeader(prev => ({ ...prev, vendorContactId: contactId }))}
                      getOptionLabel={(contact) => `${contact.contact_code} - ${contact.first_name} ${contact.last_name}`}
                      getOptionValue={(contact) => contact.contact_id.toString()}
                      getOptionSecondaryLabel={(contact) => contact.email}
                      placeholder="Select Contact"
                      disabled={!header.vendorCode || vendorContacts.length === 0}
                      className="data-contact-dropdown"
                    />
                    {vendorContacts.length === 0 && header.vendorCode && (
                      <p className="text-xs text-yellow-600">No contacts available</p>
                    )}
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Currency *</label>
                    <SearchableDropdown
                      options={dropdownData.currencies}
                      value={header.currencyCode}
                      onChange={(currencyCode) => setHeader(prev => ({ ...prev, currencyCode }))}
                      getOptionLabel={(currency) => currency.CurrencyName}
                      getOptionValue={(currency) => currency.CurrencyCode}
                      getOptionSecondaryLabel={(currency) => currency.CurrencyCode}
                      placeholder="Select Currency"
                      error={errors.currencyCode}
                      className="data-currency-dropdown"
                    />
                    {errors.currencyCode && <span className="text-xs text-red-500">{errors.currencyCode}</span>}
                  </div>
                  
                  {/* Branch Selection Field */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Branch *</label>
                    <SearchableDropdown
                      options={branches}
                      value={header.BranchCode}
                      onChange={(branchCode) => setHeader(prev => ({ ...prev, BranchCode: branchCode }))}
                      getOptionLabel={(branch) => `${branch.BranchCode} - ${branch.BranchName}`}
                      getOptionValue={(branch) => branch.BranchCode}
                      getOptionSecondaryLabel={(branch) => branch.BranchCode}
                      placeholder="Select Branch"
                      error={errors.BranchCode}
                      disabled={loadingBranches}
                      className="data-branch-dropdown"
                    />
                    {errors.BranchCode && <span className="text-xs text-red-500">{errors.BranchCode}</span>}
                  </div>
                  
                  {/* Department Selection Field - Only shown if ShowOnHeader is true */}
                  {showDepartmentField && (
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Department *</label>
                      <SearchableDropdown
                        options={departments}
                        value={header.department_id}
                        onChange={(deptId) => setHeader(prev => ({ ...prev, department_id: deptId }))}
                        getOptionLabel={(dept) => `${dept.DepartmentCode} - ${dept.DepartmentName}`}
                        getOptionValue={(dept) => dept.DepartmentID.toString()}
                        getOptionSecondaryLabel={(dept) => dept.DepartmentCode}
                        placeholder="Select Department"
                        error={errors.department_id}
                        disabled={loadingDepartments}
                        className="data-department-dropdown"
                      />
                      {errors.department_id && <span className="text-xs text-red-500">{errors.department_id}</span>}
                    </div>
                  )}
                  
                  {/* Linked Document Section */}
                   <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Linked To</label>
                    <div className="flex gap-2">
                      <select 
                        name="LinkedDocumentType" 
                        value={Array.isArray(header.LinkedDocumentType) ? header.LinkedDocumentType[0] : header.LinkedDocumentType || ''} 
                        onChange={handleLinkedDocumentTypeChange}
                        className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                      >
                        {LINKED_DOCUMENT_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <button 
                        type="button"
                        className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center ${
                          (header.LinkedDocumentType === 'SO' || (Array.isArray(header.LinkedDocumentType) && header.LinkedDocumentType[0] === 'SO'))
                            ? 'bg-[#6A89A7] text-white hover:bg-[#88BDF2]' 
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                        disabled={!header.LinkedDocumentType || 
                          (Array.isArray(header.LinkedDocumentType) ? header.LinkedDocumentType[0] !== 'SO' : header.LinkedDocumentType !== 'SO')}
                        onClick={() => {
                          const docType = Array.isArray(header.LinkedDocumentType) ? header.LinkedDocumentType[0] : header.LinkedDocumentType;
                          if (docType === 'SO') {
                            setShowSalesItemsModal(true);
                          } else {
                            alert(`Functionality for ${getLinkedDocumentLabel(docType)} is not implemented yet.`);
                          }
                        }}
                        title={Array.isArray(header.LinkedDocumentType) && header.LinkedDocumentType[0] !== 'SO' || header.LinkedDocumentType !== 'SO' ? 
                          `Functionality for ${getLinkedDocumentLabel(header.LinkedDocumentType)} is not implemented yet` : 
                          "Select linked documents"
                        }
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
                        </svg>
                        Linked Documents
                      </button>
                    </div>
                    {header.LinkedDocumentNumber && (
                      <div className="text-sm text-gray-600 mt-1">
                        Linked to: {getLinkedDocumentLabel(header.LinkedDocumentType)} - {header.LinkedDocumentNumber}
                      </div>
                    )}
                  </div>
                  
                  {/* Selected Information Display - FIXED */}
                  {(selectedAddress || selectedContact) && (
                    <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedAddress && (
                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                          <h4 className="font-medium text-blue-800 mb-2">Selected Address:</h4>
                          <div className="text-sm">
                            <p className="font-medium">Address:</p>
                            <p>{selectedAddress.address_line1}</p>
                            {selectedAddress.address_line2 && <p>{selectedAddress.address_line2}</p>}
                            <p>{selectedAddress.city}, {selectedAddress.state} {selectedAddress.zip_code}</p>
                            <p>{selectedAddress.country}</p>
                            <p className="font-medium mt-2">Contact:</p>
                            <p>{selectedAddress.contact_person_name}</p>
                            <p>{selectedAddress.contact_person_number}</p>
                            <p className="font-medium mt-2">GST Number:</p>
                            <p>{selectedAddress.gst_number}</p>
                          </div>
                        </div>
                      )}
                      
                      {selectedContact && (
                        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                          <h4 className="font-medium text-green-800 mb-2">Selected Contact:</h4>
                          <div className="text-sm">
                            <p className="font-medium">Name:</p>
                            <p>{selectedContact.first_name} {selectedContact.last_name}</p>
                            <p className="font-medium mt-2">Contact Code:</p>
                            <p>{selectedContact.contact_code}</p>
                            <p className="font-medium mt-2">Email:</p>
                            <p>{selectedContact.email}</p>
                            <p className="font-medium mt-2">Phone:</p>
                            <p>{selectedContact.phone_number}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Delivery Date</label>
                    <input 
                      type="date" 
                      name="deliveryDate" 
                      value={header.deliveryDate} 
                      onChange={handleHeaderChange} 
                      className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Payment Terms</label>
                    <SearchableDropdown
                      options={PAYMENT_TERMS_OPTIONS}
                      value={header.paymentTerms}
                      onChange={(paymentTerm) => setHeader(prev => ({ ...prev, paymentTerms: paymentTerm }))}
                      getOptionLabel={(option) => option.label}
                      getOptionValue={(option) => option.value}
                      placeholder="Select Payment Terms"
                      className="data-payment-dropdown"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Warehouse *</label>
                    <SearchableDropdown
                      options={dropdownData.locations}
                      value={header.locationCode}
                      onChange={(locationCode) => setHeader(prev => ({ ...prev, locationCode }))}
                      getOptionLabel={(location) => location.LocationName}
                      getOptionValue={(location) => location.LocationCode}
                      getOptionSecondaryLabel={(location) => location.LocationCode}
                      placeholder="Select Warehouse"
                      error={errors.locationCode}
                      className="data-warehouse-dropdown"
                    />
                    {errors.locationCode && <span className="text-xs text-red-500">{errors.locationCode}</span>}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Status</label>
                    <SearchableDropdown
                      options={STATUS_OPTIONS}
                      value={header.is_active}
                      onChange={(status) => setHeader(prev => ({ ...prev, is_active: status }))}
                      getOptionLabel={(option) => option.label}
                      getOptionValue={(option) => option.value}
                      placeholder="Select Status"
                      className="data-status-dropdown"
                    />
                  </div>
                  <div className="flex flex-col gap-2 md:col-span-2">
                    <label className="block text-sm font-medium text-[#64748b]">Remarks</label>
                    <textarea 
                      name="remarks" 
                      value={header.remarks} 
                      onChange={handleHeaderChange} 
                      rows={3}
                      className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                    />
                  </div>
                </div>
              </div>
              
              {/* PO Items */}
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-[#384959]">Purchase Order Items</h3>
                  <div className="flex gap-2">
                    {savedItems.length > 0 && (
                      <button 
                        type="button" 
                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm flex items-center"
                        onClick={loadSavedItemsFromTable}
                        title="Load saved items from table"
                      >
                        <SaveIcon className="mr-1" size={12} /> Reload Saved
                      </button>
                    )}
                    {/* <button 
                      type="button" 
                      className="px-4 py-2 bg-[#6A89A7] text-white rounded-lg hover:bg-[#88BDF2] flex items-center transition-colors" 
                      onClick={() => setShowProductMultiSelectModal(true)}
                    >
                      <PlusIcon className="mr-1" /> Multi-Select Products
                    </button> */}
                    <button 
                      type="button" 
                      className="px-4 py-2 bg-[#6A89A7] text-white rounded-lg hover:bg-[#88BDF2] flex items-center transition-colors" 
                      onClick={addItem}
                    >
                      <PlusIcon className="mr-1" /> Add Item
                    </button>
                  </div>
                </div>
                
                {/* Always show the table header */}
                <div className="overflow-x-auto custom-scrollbar">
                  <table className="min-w-full border border-[#E2E8F0]">
                    <thead>
                      <tr className="bg-[#BDDDFC] text-[#384959]">
                        <th className="p-2">#</th>
                        <th className="p-2">Product</th>
                        <th className="p-2">Product Code</th>
                        <th className="p-2">Description</th>
                        <th className="p-2">UOM</th>
                        <th className="p-2">Qty</th>
                        <th className="p-2">Remaining Qty</th>
                        <th className="p-2">Unit Price</th>
                        <th className="p-2">Discount</th>
                        <th className="p-2">Tax</th>
                        <th className="p-2">Tax Rate</th>
                        <th className="p-2">Tax Amount</th>
                        <th className="p-2">Total</th>
                        {/* Department column - Only shown if ShowOnItem is true */}
                        {showDepartmentItemField && <th className="p-2">Department</th>}
                        <th className="p-2">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length > 0 ? (
                        items.map((item, idx) => {
                          // Find the selected tax code to display its rate
                          const selectedTax = dropdownData.taxCodes.find(t => t.TaxCode === item.taxCode);
                          const taxRate = selectedTax && selectedTax.TaxPercentage ? parseFloat(selectedTax.TaxPercentage) : 0;
                          
                          return (
                            <tr key={idx} className={`border-t border-[#E2E8F0] ${item.isLinked ? 'bg-blue-50' : ''}`}>
                              <td className="p-2 text-center">{idx + 1}</td>
                              <td className="p-2">
                                <SearchableDropdown
                                  options={dropdownData.products}
                                  value={item.productCode}
                                  onChange={(productCode) => handleProductChange(idx, productCode)}
                                  onMultiSelectTrigger={() => setShowProductMultiSelectModal(true)}
                                  multiSelectShortcut="p"
                                  getOptionLabel={(product) => product.ProductName}
                                  getOptionValue={(product) => product.ProductCode}
                                  getOptionSecondaryLabel={(product) => product.ProductCode}
                                  placeholder="Select Product"
                                  width="w-40"
                                  error={errors[`productName_${idx}`]}
                                  disabled={item.isLinked}
                                  className="data-product-dropdown"
                                />
                                {errors[`productName_${idx}`] && <span className="text-xs text-red-500">*</span>}
                              </td>
                              <td className="p-2">
                                <input 
                                  name="productCodeDisplay" 
                                  value={item.productCode} 
                                  readOnly 
                                  className="w-24 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9] uppercase"
                                  title="Auto-populated from product selection"
                                />
                              </td>
                              <td className="p-2">
                                <input 
                                  name="description" 
                                  value={item.description} 
                                  onChange={e => handleItemChange(idx, e)} 
                                  disabled={item.isLinked}
                                  className={`w-40 border rounded-lg px-2 py-1 ${errors[`description_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] uppercase ${item.isLinked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                />
                                {errors[`description_${idx}`] && <span className="text-xs text-red-500">*</span>}
                              </td>
                              <td className="p-2">
                                <input 
                                  name="uom" 
                                  value={item.uom} 
                                  readOnly 
                                  className="w-16 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9] uppercase"
                                  title="Auto-populated from product selection"
                                />
                                {errors[`uom_${idx}`] && <span className="text-xs text-red-500">*</span>}
                              </td>
                              <td className="p-2">
                                <input 
                                  name="quantity" 
                                  type="number" 
                                  min="0" 
                                  value={item.quantity} 
                                  onChange={e => handleItemChange(idx, e)} 
                                  disabled={item.isLinked}
                                  className={`w-16 border rounded-lg px-2 py-1 ${errors[`quantity_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] uppercase ${item.isLinked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                />
                                {errors[`quantity_${idx}`] && <span className="text-xs text-red-500">*</span>}
                              </td>
                              <td className="p-2">
                                <input 
                                  name="remainingQuantity" 
                                  type="number" 
                                  min="0" 
                                  value={item.remainingQuantity} 
                                  readOnly
                                  onChange={e => handleItemChange(idx, e)} 
                                  className={`w-16 border rounded-lg px-2 py-1 ${errors[`quantity_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC uppercase ${item.isLinked ? 'bg-gray-100' : ''}`}
                                />
                                {errors[`quantity_${idx}`] && <span className="text-xs text-red-500">*</span>}
                              </td>
                              <td className="p-2">
                                <input 
                                  name="unitPrice" 
                                  type="number" 
                                  min="0" 
                                  step="0.01"
                                  value={item.unitPrice} 
                                  onChange={e => handleItemChange(idx, e)} 
                                  disabled={item.isLinked}
                                  className={`w-20 border rounded-lg px-2 py-1 ${errors[`unitPrice_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] uppercase ${item.isLinked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                />
                                {errors[`unitPrice_${idx}`] && <span className="text-xs text-red-500">*</span>}
                              </td>
                              <td className="p-2">
                                <input 
                                  name="discount" 
                                  type="number" 
                                  min="0" 
                                  step="0.01"
                                  value={item.discount} 
                                  onChange={e => handleItemChange(idx, e)} 
                                  disabled={item.isLinked}
                                  className={`w-16 border rounded-lg px-2 py-1 border-[#E2E8F0] focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] uppercase ${item.isLinked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                />
                              </td>
                              <td className="p-2">
                                <SearchableDropdown
                                  options={dropdownData.taxCodes}
                                  value={item.taxCode}
                                  onChange={(taxCode) => {
  const newItems = [...items];
  newItems[idx].taxCode = taxCode;
  
  // Calculate tax immediately when tax code changes
  const { total, taxAmount, taxRate } = calculateItemTax(newItems[idx]);
  newItems[idx].total = total;
  newItems[idx].taxAmount = taxAmount;
  newItems[idx].taxRate = taxRate.toString();
  
  setItems(newItems);
}}
                                  getOptionLabel={(tax) => `${tax.TaxName} (${tax.TaxCode})`}
                                  getOptionValue={(tax) => tax.TaxCode}
                                  getOptionSecondaryLabel={(tax) => `${tax.TaxPercentage}%`}
                                  placeholder="Select Tax"
                                  width="w-30"
                                  error={errors[`taxCode_${idx}`]}
                                  disabled={item.isLinked}
                                  className="data-tax-dropdown"
                                />
                                {errors[`taxCode_${idx}`] && <span className="text-xs text-red-500">*</span>}
                              </td>
                            
<td className="p-2">
  <div className="flex items-center">
    <span className="text-sm">{item.taxRate}%</span>
  </div>
</td>
                              <td className="p-2">
                                <input 
                                  name="taxAmount" 
                                  value={item.taxAmount} 
                                  readOnly 
                                  className="w-16 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9]"
                                />
                              </td>
                              <td className="p-2">
                                <input 
                                  name="total" 
                                  value={item.total} 
                                  readOnly 
                                  className="w-20 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9]"
                                />
                              </td>
                              {/* Department cell - Only shown if ShowOnItem is true */}
                              {showDepartmentItemField && (
                                <td className="p-2">
                                  <SearchableDropdown
                                    options={departments}
                                    value={item.department_id}
                                    onChange={(deptId) => {
                                      const newItems = [...items];
                                      newItems[idx].department_id = deptId;
                                      setItems(newItems);
                                    }}
                                    getOptionLabel={(dept) => `${dept.DepartmentCode} - ${dept.DepartmentName}`}
                                    getOptionValue={(dept) => dept.DepartmentID.toString()}
                                    getOptionSecondaryLabel={(dept) => dept.DepartmentCode}
                                    placeholder="Select"
                                    width="w-32"
                                    error={errors[`department_id_${idx}`]}
                                    disabled={item.isLinked}
                                    className="data-item-department-dropdown"
                                  />
                                  {errors[`department_id_${idx}`] && <span className="text-xs text-red-500">*</span>}
                                </td>
                              )}
                              <td className="p-2 text-center">
                                <button 
                                  type="button" 
                                  className="text-red-500 hover:text-red-700 transition-colors" 
                                  onClick={() => removeItem(idx)}
                                >
                                  <TrashIcon />
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={showDepartmentItemField ? 15 : 14} className="p-8 text-center">
                            <div className="flex flex-col items-center justify-center">
                              <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
                              </svg>
                              <h3 className="text-lg font-medium text-gray-900 mb-1">No items added</h3>
                              <p className="text-gray-500 text-center max-w-md">
                                Click the "Add Item" button to add items to this purchase order, or link items from a sales order if applicable.
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                
                {/* Total Summary Section - Only show when there are items with data */}
                {hasItemsWithData && (
                  <div className="mt-6 bg-white p-4 rounded-lg border border-[#E2E8F0]">
                    <h4 className="text-md font-semibold text-[#384959] mb-4">Total Summary</h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500">Subtotal</span>
                        <span className="text-base font-semibold">{summary.subtotal}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500">Total Discount</span>
                        <span className="text-base font-semibold">{summary.totalDiscount}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500">Total Tax</span>
                        <span className="text-base font-semibold">{summary.totalTax}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500">Grand Total</span>
                        <span className="text-base font-semibold text-lg text-blue-600">{summary.grandTotal}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </form>
          </div>
          
          {/* Fixed Footer with Buttons */}
          <div className="flex-shrink-0 p-8 pt-0">
            <div className="flex justify-end gap-3">
              <button 
                type="button" 
                className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]" 
                onClick={onClose}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="px-6 py-3 rounded-lg text-white font-semibold shadow-lg transition-all duration-300 ease-in-out transform hover:scale-105 bg-[#6A89A7] hover:bg-[#88BDF2] disabled:opacity-50 disabled:cursor-not-allowed" 
                disabled={loading || isSubmitting || !isUserValid}
                onClick={handleSubmit}
              >
                {loading || isSubmitting ? 'Saving...' : (isEdit ? 'Update PO' : 'Save PO')}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Sales Items Modal */}
      <SalesItemsModal
        open={showSalesItemsModal && (Array.isArray(header.LinkedDocumentType) ? header.LinkedDocumentType[0] === 'SO' : header.LinkedDocumentType === 'SO')}
        onClose={() => setShowSalesItemsModal(false)}
        onItemsSelected={handleLinkedItemsSelected}
        existingItems={items}
      />
      
      {/* Product Multi-Select Modal */}
      <ProductMultiSelectModal
        open={showProductMultiSelectModal}
        onClose={() => setShowProductMultiSelectModal(false)}
        products={dropdownData.products}
        onConfirm={handleMultiSelectProducts}
        onSaveToTable={savePopupItemsToTable}
        onAutofill={autofillSelectedProducts}
      />
    </>
  );
};

export default PurchaseOrderFormModal;